import { findBand, BAND_COLORS } from "../bands";

// ASDAS — Ankylosing Spondylitis Disease Activity Score, CRP (preferred) or ESR.
// ⚠️ Verify clinically before relying on results.
const asdasBands = [
    { label: "Inactive disease", color: BAND_COLORS.inactive, lt: 1.3 },
    { label: "Low disease activity", color: BAND_COLORS.low, lt: 2.1 },
    { label: "High disease activity", color: BAND_COLORS.high, lte: 3.5 },
    { label: "Very high disease activity", color: BAND_COLORS.veryHigh },
];

export const asdas = {
    id: "asdas",
    name: "ASDAS",
    subtitle: "Ankylosing spondylitis disease activity score",
    group: "Axial spondyloarthritis",
    status: "ready",
    layout: "stacked",
    intro: "Answer the following questions relating to the past week.",
    options: [
        { key: "type", label: "Acute-phase reactant", choices: ["CRP", "ESR"], default: "CRP" },
    ],
    fields: [
        {
            key: "backPain",
            label: "Overall level of inflammatory neck, back or hip pain",            hint: "None: 0 · Very severe: 10",
            type: "number",
            min: 0,
            max: 10,
            step: 0.1,
        },
        {
            key: "peripheralPain",
            label: "Overall level of pain/swelling in joints other than neck, back or hips",            hint: "None: 0 · Very severe: 10",
            type: "number",
            min: 0,
            max: 10,
            step: 0.1,
        },
        {
            key: "morningStiffness",
            label: "Duration of morning stiffness from the time you wake up",            hint: "0 hrs: 0 · ½ hr: 2.5 · 1 hr: 5 · 1½ hr: 7.5 · ≥2 hrs: 10",
            type: "number",
            min: 0,
            max: 10,
            step: 0.5,
        },
        {
            key: "ptGlobal",
            label: "Patient global assessment of disease activity",            hint: "None: 0 · Very active: 10",
            type: "number",
            min: 0,
            max: 10,
            step: 0.1,
        },
        {
            key: "marker",
            label: (o) => (o.type === "CRP" ? "CRP" : "ESR"),
            unit: (o) => (o.type === "CRP" ? "mg/L" : "mm/h"),
            hint: (o) =>
                o.type === "CRP"
                    ? "CRP = C-reactive protein."
                    : "ESR = erythrocyte sedimentation rate.",
            type: "number",
            min: (o) => (o.type === "CRP" ? 2 : 1),
            max: (o) => (o.type === "CRP" ? 500 : 200),
            step: 0.1,
        },
    ],
    compute: (v, o) => {
        const { backPain, morningStiffness, ptGlobal, peripheralPain, marker } = v;
        if (o.type === "CRP") {
            return (
                0.12 * backPain +
                0.06 * morningStiffness +
                0.11 * ptGlobal +
                0.07 * peripheralPain +
                0.58 * Math.log(marker + 1)
            );
        }
        // ESR variant
        return (
            0.08 * backPain +
            0.07 * morningStiffness +
            0.11 * ptGlobal +
            0.09 * peripheralPain +
            0.29 * Math.sqrt(marker)
        );
    },
    interpret: (score) => findBand(score, asdasBands),
    bands: () => asdasBands,
    about:
        "The Ankylosing Spondylitis Disease Activity Score (ASDAS) is an ASAS-endorsed composite index combining back pain, duration of morning stiffness, patient global assessment, peripheral pain/swelling and an acute-phase reactant. The CRP version is the preferred version; the ESR version is an alternative when CRP is unavailable.",
    references: [
        "Lukas C, et al. Development of an ASAS-endorsed disease activity score (ASDAS) in patients with ankylosing spondylitis. Ann Rheum Dis. 2009.",
        "Machado P, et al. ASDAS: defining cut-off values for disease activity states and improvement scores. Ann Rheum Dis. 2011.",
    ],
};
